import React, { useEffect, useState } from "react";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import { apiClient } from "../../services/apiClient";

/**
 * Bloque B.8 — Detalle por tienda de Reposición + Muestras nuevas.
 *
 * Reutiliza el endpoint `GET /distribution/seeding/samples-detail/:productId`
 * (Bloque B.4) que retorna entries con campo `sourceType` ('seeding' o
 * 'reposicion'). El componente agrupa por tienda y muestra ambas categorías
 * con un chip diferenciador, ordenando por qty desc.
 */

export interface StoreReplenishmentDetailDialogProps {
  open: boolean;
  unifiedProductId: string | null;
  productName: string;
  onClose: () => void;
}

interface DetailRow {
  storeName: string;
  qty: number;
  status: string;
  sourceType: "seeding" | "reposicion";
  abcClass: string | null;
  reason: string | null;
}

export const StoreReplenishmentDetailDialog: React.FC<
  StoreReplenishmentDetailDialogProps
> = ({ open, unifiedProductId, productName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DetailRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !unifiedProductId) {
      setRows([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<{ data: DetailRow[] }>(
          `/distribution/seeding/samples-detail/${encodeURIComponent(
            unifiedProductId,
          )}`,
        );
        if (cancelled) return;
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        // Ordena por qty desc (las tiendas con más déficit/seeding arriba).
        data.sort((a: DetailRow, b: DetailRow) => (b.qty ?? 0) - (a.qty ?? 0));
        setRows(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error cargando detalle");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, unifiedProductId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Detalle por tienda — Reposición + Muestras nuevas
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ wordBreak: "break-word", mb: 1.5 }}
        >
          {productName}
        </Typography>
        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin recomendaciones de reposición ni muestras nuevas para este
            producto.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tienda</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>ABC</TableCell>
                <TableCell>Razón</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={`${row.storeName}-${row.sourceType}-${idx}`}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {row.storeName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                      {row.qty}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        row.sourceType === "reposicion"
                          ? "Reposición"
                          : "Muestra nueva"
                      }
                      color={
                        row.sourceType === "reposicion" ? "primary" : "success"
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        color:
                          row.status === "approved"
                            ? "success.main"
                            : "warning.main",
                      }}
                    >
                      {row.status === "approved" ? "Aprobada" : "Pendiente"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                      }}
                    >
                      {row.abcClass || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {row.reason || "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
